import React, { useEffect, useState } from "react";
import { EmployeeInfo } from "../interfaces/EmployeeInfoInterface";
import {
  EmployeePWClassificationRecord,
  EmployeePWClassificationResponse,
  PWClassification,
} from "../interfaces/PrevailingWage";
import {
  getEmployeePWClassification,
  getPWClassifications,
  postEmployeePWClassification,
} from "../api/prevailingWage";
import { Layers, Clock, X, Check, Info, AlertTriangle } from "lucide-react";

/**
 * PW Classification assignment — v2 modal (Employees page).
 * Rendered inside the page's `.emp` scope, so it reuses the shared
 * emp-modal / emp-to-* classes from EmployeeView.css (same structure as the
 * Time Off Policy modal: identity card → current card → history timeline →
 * effective-dated assign form).
 */
interface EmployeePWClassificationModalProps {
  isOpen: boolean;
  employee: EmployeeInfo;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function initials(first: string, last: string): string {
  return `${(first || "?").charAt(0)}${(last || "").charAt(0)}`.toUpperCase();
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "YYYY-MM-DD"/ISO → "Jan 1, 2026" without timezone shift. */
function fmtISO(dateStr: string | null): string {
  if (!dateStr) return "—";
  const p = dateStr.slice(0, 10).split("-");
  if (p.length !== 3) return dateStr;
  const mo = parseInt(p[1], 10) - 1;
  if (mo < 0 || mo > 11) return dateStr;
  return `${MONTHS[mo]} ${parseInt(p[2], 10)}, ${p[0]}`;
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function classificationLabel(c: PWClassification): string {
  return `${c.name}${c.altPWClassificationID ? ` (${c.altPWClassificationID})` : ""}`;
}

const EmployeePWClassificationModal: React.FC<EmployeePWClassificationModalProps> = ({
  isOpen,
  employee,
  onClose,
  onSaved,
}) => {
  const [classifications, setClassifications] = useState<PWClassification[]>([]);
  const [current, setCurrent] = useState<EmployeePWClassificationRecord | null>(null);
  const [history, setHistory] = useState<EmployeePWClassificationRecord[]>([]);
  const [selectedClassificationID, setSelectedClassificationID] = useState<number>(0);
  const [effectiveDate, setEffectiveDate] = useState<string>("");
  const [dateError, setDateError] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setEffectiveDate(todayISO());
    setNotes("");
    setError(null);
    setDateError(false);
    setDataFetched(false);
    setLoading(true);
    Promise.all([getEmployeePWClassification(employee.employeeID), getPWClassifications(true)])
      .then(([pwData, classData]: [EmployeePWClassificationResponse, PWClassification[]]) => {
        setCurrent(pwData.current);
        setHistory(pwData.history);
        setClassifications(classData);
        setSelectedClassificationID(
          pwData.current?.pwClassificationID ?? (classData[0]?.pwClassificationID ?? 0)
        );
        setDataFetched(true);
      })
      .catch(() => setError("Failed to load PW classification data."))
      .finally(() => setLoading(false));
  }, [isOpen, employee.employeeID]);

  // Esc closes (matching the page's other v2 modals).
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [isOpen, saving, onClose]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!effectiveDate) {
      setDateError(true);
      return;
    }
    if (!selectedClassificationID) return;
    setSaving(true);
    setError(null);
    try {
      await postEmployeePWClassification(employee.employeeID, {
        pwClassificationID: selectedClassificationID,
        effectiveDate,
        notes: notes.trim() || null,
      });
      await onSaved(); // parent refreshes the roster and closes the modal
    } catch {
      setError("Failed to save. Please try again.");
      setSaving(false);
    }
  };

  const selectedDescription = classifications.find(
    (c) => c.pwClassificationID === selectedClassificationID
  )?.description;
  const isBackdated = !!(current && effectiveDate && effectiveDate < current.effectiveDate.slice(0, 10));
  // Timeline = the open (current) record first, then closed history records.
  const timelineItems = [...(current ? [current] : []), ...history];

  return (
    <div className="emp-modal-bg" onClick={() => !saving && onClose()}>
      <div className="emp-modal emp-modal-wide" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="emp-modal-head">
          <div className="mi">
            <Layers size={21} />
          </div>
          <div>
            <h3>PW Classification</h3>
            <div className="ms">
              {employee.firstName} {employee.lastName} · {employee.altEmployeeID || "—"}
            </div>
          </div>
          <button type="button" className="x" onClick={onClose} disabled={saving}>
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="emp-modal-body">
          {/* Identity card */}
          <div className="emp-confirm-emp">
            <span className="av">{initials(employee.firstName, employee.lastName)}</span>
            <div>
              <div className="nm">
                {employee.firstName} {employee.lastName}
              </div>
              <div className="who-sub emp-mono">
                {employee.altEmployeeID || "—"}
                {employee.jobTitle ? ` · ${employee.jobTitle}` : ""}
              </div>
            </div>
          </div>

          {loading && (
            <div className="emp-to-loading">
              <span className="emp-spin" /> Loading classification data…
            </div>
          )}

          {error && (
            <div className="emp-to-warn-banner">
              <AlertTriangle size={17} />
              <div>{error}</div>
            </div>
          )}

          {!loading && dataFetched && (
            <>
              {/* Current assignment */}
              <div className="emp-to-subhead">Current classification</div>
              {current ? (
                <div className="emp-to-cur-card">
                  <div className="emp-to-cur-top">
                    <span className="emp-to-cur-ic">
                      <Layers size={18} />
                    </span>
                    <div className="emp-to-cur-body">
                      <div className="emp-to-cur-name">
                        {current.classificationName}
                        {current.altPWClassificationID ? ` (${current.altPWClassificationID})` : ""}
                      </div>
                      <div className="emp-to-cur-meta">
                        <span className="emp-to-cur-since">
                          <Clock size={13} />
                          Since {fmtISO(current.effectiveDate)}
                        </span>
                      </div>
                    </div>
                    <span className="emp-cur-badge">
                      <span className="dot" />
                      Current
                    </span>
                  </div>
                  {current.notes && <div className="emp-to-cur-notes">{current.notes}</div>}
                </div>
              ) : (
                <div className="emp-to-empty">
                  <Info size={19} />
                  <div>
                    <div className="emp-to-empty-t">No PW classification assigned</div>
                    <div className="emp-to-empty-s">
                      Assign a classification below to include this employee in prevailing wage
                      reporting.
                    </div>
                  </div>
                </div>
              )}

              {/* History timeline */}
              {history.length > 0 && (
                <>
                  <div className="emp-to-divider" />
                  <div className="emp-to-subhead">Assignment history</div>
                  <div className="emp-to-timeline">
                    {timelineItems.map((item) => {
                      const open = item.endDate === null;
                      return (
                        <div
                          key={item.employeePWClassificationID}
                          className={`emp-to-tl-item${open ? " cur" : ""}`}
                        >
                          <span className="emp-to-tl-dot" />
                          <div className="emp-to-tl-body">
                            <div className="emp-to-tl-head">
                              <span className="emp-to-tl-name">{item.classificationName}</span>
                              {open && (
                                <span className="emp-cur-badge">
                                  <span className="dot" />
                                  Current
                                </span>
                              )}
                            </div>
                            <div className="emp-to-tl-range emp-mono">
                              {fmtISO(item.effectiveDate)} →{" "}
                              {open ? <span className="emp-to-tl-present">Present</span> : fmtISO(item.endDate)}
                            </div>
                            {item.notes && <div className="emp-to-tl-notes">{item.notes}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Assign / Change form */}
              <div className="emp-to-divider" />
              <div className="emp-to-subhead">{current ? "Change classification" : "Assign classification"}</div>

              <div className="emp-to-frow">
                <label>
                  PW classification <span className="emp-req">*</span>
                </label>
                <select
                  className="emp-select"
                  value={selectedClassificationID}
                  onChange={(e) => setSelectedClassificationID(parseInt(e.target.value, 10))}
                  disabled={saving}
                >
                  {classifications.map((c) => (
                    <option key={c.pwClassificationID} value={c.pwClassificationID}>
                      {classificationLabel(c)}
                    </option>
                  ))}
                </select>
                {selectedDescription && <div className="emp-to-help">{selectedDescription}</div>}
              </div>

              <div className="emp-to-frow2">
                <div className="emp-to-frow" style={{ marginBottom: 0 }}>
                  <label>
                    Effective date <span className="emp-req">*</span>
                  </label>
                  <input
                    type="date"
                    className={`emp-input${dateError ? " emp-to-err" : ""}`}
                    value={effectiveDate}
                    onChange={(e) => {
                      setEffectiveDate(e.target.value);
                      if (e.target.value) setDateError(false);
                    }}
                    disabled={saving}
                  />
                  {dateError && (
                    <div className="emp-to-inline-err">
                      <AlertTriangle size={15} />
                      An effective date is required.
                    </div>
                  )}
                </div>
                <div className="emp-to-frow" style={{ marginBottom: 0 }}>
                  <label>
                    Notes <span className="emp-to-opt">(optional)</span>
                  </label>
                  <input
                    type="text"
                    className="emp-input"
                    placeholder="e.g. Reclassified to Pipefitter"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={saving}
                  />
                </div>
              </div>

              {isBackdated && (
                <div className="emp-to-warn-banner">
                  <AlertTriangle size={17} />
                  <div>
                    This date is <b>before</b> the current classification began (
                    {fmtISO(current!.effectiveDate)}). Backdating changes which rate applies to
                    already-worked days — double-check the effective date.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="emp-modal-foot">
          <button type="button" className="emp-btn" onClick={onClose} disabled={saving}>
            {dataFetched ? "Cancel" : "Close"}
          </button>
          {dataFetched && (
            <button
              type="button"
              className="emp-btn primary"
              onClick={handleSave}
              disabled={saving || !selectedClassificationID}
            >
              {saving ? (
                <>
                  <span className="emp-spin" /> Saving…
                </>
              ) : (
                <>
                  <Check size={16} />
                  {current ? "Save classification" : "Assign classification"}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeePWClassificationModal;
